import type { Goal } from '@qi/shared';
import { copy } from '@qi/shared';
import {
  AuroraMesh,
  BeforeAfterPeek,
  buttonClasses,
  Chip,
  Faq,
  GradientHairline,
  OrbVisualizer,
  SectionReveal,
  StepRail,
  type StepRailItem,
  TrustCard,
  Wordmark,
} from '@qi/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Activity,
  BookOpen,
  Camera,
  Check,
  Crosshair,
  Globe,
  Lock,
  Mic,
  Moon,
  Sparkles,
  Waves,
  Wind,
  Zap,
} from 'lucide-react';
import { InteractiveResonance } from '../components/landing/InteractiveResonance.tsx';
import { PricingPlans } from '../components/marketing/PricingPlans.tsx';

export const Route = createFileRoute('/_marketing/')({
  component: LandingPage,
});

const STEPS: StepRailItem[] = [
  {
    icon: <Activity size={24} strokeWidth={1.6} />,
    title: 'Capture',
    body: 'Your webcam and mic measure blink, breath, facial stability, and voice — locally.',
  },
  {
    icon: <Waves size={24} strokeWidth={1.6} />,
    title: 'Resonate',
    body: 'Adaptive harmonic sweeps surface which frequencies your nervous system responds to.',
  },
  {
    icon: <Sparkles size={24} strokeWidth={1.6} />,
    title: 'Personalize',
    body: 'AI builds a frequency protocol tuned to the goal you picked. Press play.',
  },
];

const PRELUDE = [
  { icon: <Zap size={12} strokeWidth={2} />, label: '30s setup' },
  { icon: <Camera size={12} strokeWidth={2} />, label: 'Webcam + mic' },
  { icon: <Lock size={12} strokeWidth={2} />, label: 'Nothing recorded' },
  { icon: <Check size={12} strokeWidth={2} />, label: 'Free first scan' },
] as const;

// 6 most-picked goals as a deep-link surface in the hero. The other 4 still
// live in /onboarding — this is a "pick your shortcut" affordance, not the
// full picker.
const HERO_GOALS: { id: Goal; icon: React.ReactNode }[] = [
  { id: 'stress', icon: <Wind size={12} strokeWidth={2} /> },
  { id: 'sleep', icon: <Moon size={12} strokeWidth={2} /> },
  { id: 'focus', icon: <Crosshair size={12} strokeWidth={2} /> },
  { id: 'recovery', icon: <Activity size={12} strokeWidth={2} /> },
  { id: 'energy', icon: <Zap size={12} strokeWidth={2} /> },
  { id: 'meditation', icon: <Sparkles size={12} strokeWidth={2} /> },
];

const FOUNDATIONS = [
  {
    icon: <BookOpen size={20} strokeWidth={1.6} />,
    title: 'Solfeggio tradition',
    body: '174 · 285 · 396 · 417 · 528 · 639 · 741 · 852 · 963 Hz. The historical resonance set, sequenced by intent.',
  },
  {
    icon: <Globe size={20} strokeWidth={1.6} />,
    title: 'Schumann resonance',
    body: 'Earth’s electromagnetic fundamental at 7.83 Hz and its harmonics — grounding states, real time.',
  },
  {
    icon: <Activity size={20} strokeWidth={1.6} />,
    title: 'Biofeedback principles',
    body: 'Blink, breath, facial stability, and voice are how your nervous system shows its work.',
  },
] as const;

// Paraphrased beta-cohort feedback themes — never invent named testimonials
// for a pre-launch product. The footer attribution makes the framing honest.
const VOICES = [
  {
    quote: "The frequencies that worked weren't the ones I expected.",
    theme: 'Signal over guess',
  },
  {
    quote:
      'Sixty seconds in, I could feel the shift. The before/after made it impossible to dismiss.',
    theme: 'Immediate read',
  },
  {
    quote: "It's the only one that adapts. The rest just play a track and hope.",
    theme: 'Adaptive vs fixed',
  },
] as const;

// Side-by-side differentiation vs "most meditation apps". Names the category
// without naming a specific competitor — keeps the page evergreen.
const THEM = [
  'One fixed track for everyone',
  'No feedback from your nervous system',
  'The protocol never adapts to you',
  'You guess if it worked',
] as const;
const US = [
  'Webcam + mic measure your biosignals — locally',
  'Adaptive sweep finds frequencies that resonate for you',
  'Protocol updates after every scan',
  'Before/after shows the shift — every session',
] as const;

const FAQS = [
  {
    q: 'Do I need headphones?',
    a: 'Headphones make the harmonic protocols feel more immersive, but they’re not required. Built-in laptop or phone speakers work for the scan and most sessions.',
  },
  {
    q: 'How long does a scan take?',
    a: 'About 60 seconds end to end — a short bioscan, a guided voice phrase, and an adaptive frequency sweep.',
  },
  {
    q: 'What does Qi Scan actually see through my webcam?',
    a: 'Blink rate, breath cadence, and facial stability. Frames are processed in-browser and discarded. Nothing is uploaded.',
  },
  {
    q: 'Is this medical advice?',
    a: 'No. Qi Scan supports wellness optimization — sleep, focus, recovery, emotional coherence. It does not diagnose or replace medical professionals.',
  },
  {
    q: 'How is this different from a meditation app?',
    a: 'Most apps deliver one fixed audio track. Qi Scan measures your nervous system’s response and tunes the frequency protocol to it. The protocol changes as you do.',
  },
  {
    q: 'What happens to my data if I cancel?',
    a: 'One click in Settings removes your account and every scan summary. We never sell biometric data. We never will.',
  },
] as const;

function LandingPage() {
  return (
    <div>
      <Hero />
      <GradientHairline className="mx-auto max-w-6xl" />
      <SectionReveal>
        <TryItLive />
      </SectionReveal>
      <SectionReveal>
        <HowItWorks />
      </SectionReveal>
      <SectionReveal>
        <Differentiation />
      </SectionReveal>
      <SectionReveal>
        <Voices />
      </SectionReveal>
      <SectionReveal>
        <Foundations />
      </SectionReveal>
      <SectionReveal>
        <TrustStrip />
      </SectionReveal>
      <SectionReveal>
        <ProofPeek />
      </SectionReveal>
      <SectionReveal>
        <PricingTeaser />
      </SectionReveal>
      <SectionReveal>
        <Faqs />
      </SectionReveal>
      <FinalCta />
      <Disclaimer />
    </div>
  );
}

function Hero() {
  return (
    <section
      aria-labelledby="hero-heading"
      className="relative overflow-hidden px-6 pt-10 pb-16 md:pt-14"
    >
      <AuroraMesh intensity="standard" position="top" />
      <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-8">
        {/* Copy column */}
        <div className="flex flex-col items-center gap-6 text-center md:col-span-7 md:items-start md:text-left">
          <Wordmark size="lg" />
          <h1
            id="hero-heading"
            className="text-balance font-display text-4xl leading-[1.05] text-ink-0 md:text-6xl"
          >
            Recover, Re-Energize, and Restore Balance.
          </h1>
          <p className="max-w-2xl text-pretty text-lg text-ink-1 md:text-xl">
            Feel calmer and more energized in minutes. Qi Scan reads how your nervous system
            responds, then tunes the exact frequencies it needs — using just your webcam and mic,
            right in your browser. <span className="text-aurora">No app to install.</span>
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 pt-2 md:justify-start">
            {PRELUDE.map((item) => (
              <Chip key={item.label} tone="aurora">
                <span className="inline-flex items-center gap-1.5 text-aurora">
                  {item.icon}
                  <span className="text-ink-1">{item.label}</span>
                </span>
              </Chip>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-3 md:justify-start">
            <Link
              to="/onboarding"
              className={`${buttonClasses({ variant: 'resonance', size: 'lg' })} group`}
            >
              <span>Start my free scan</span>
              <span
                aria-hidden="true"
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              >
                →
              </span>
            </Link>
            <a
              href="#how-it-works"
              className={`${buttonClasses({ variant: 'ghost', size: 'lg' })} group`}
            >
              <span>See how it works</span>
              <span
                aria-hidden="true"
                className="transition-transform duration-200 group-hover:translate-y-0.5"
              >
                ↓
              </span>
            </a>
          </div>

          <p className="inline-flex items-center gap-2 text-sm text-ink-2">
            <Lock size={13} strokeWidth={2} className="shrink-0 text-jade" aria-hidden="true" />
            Nothing is recorded or uploaded — everything stays in your browser.
          </p>

          {/* Goal shortcut row — deep links into /onboarding pre-selected. */}
          <nav
            aria-labelledby="goal-shortcut-label"
            className="flex flex-col items-center gap-3 pt-4 md:items-start"
          >
            <p
              id="goal-shortcut-label"
              className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3"
            >
              Or jump straight to a goal
            </p>
            <ul className="flex flex-wrap items-center justify-center gap-2 md:justify-start">
              {HERO_GOALS.map((g) => (
                <li key={g.id}>
                  <Link
                    to="/onboarding"
                    search={{ goal: g.id }}
                    className="inline-flex items-center gap-1.5 rounded-pill border border-hairline bg-surface-3 px-3 py-1.5 text-sm text-ink-0 transition-all hover:border-aurora/40 hover:bg-[color-mix(in_oklab,var(--aurora)_10%,var(--surface-3))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora/60"
                  >
                    <span className="text-aurora">{g.icon}</span>
                    <span>{copy.goalCopy[g.id].title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Orb column — visually anchors the right side at md+, hides on mobile
            to keep copy front and center on small screens. */}
        <div className="hidden items-center justify-center md:col-span-5 md:flex">
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 -z-10 rounded-full"
              style={{ boxShadow: 'var(--glow-aurora)' }}
            />
            <OrbVisualizer size={340} rings={4} />
          </div>
        </div>
      </div>
    </section>
  );
}

function TryItLive() {
  return (
    <section aria-labelledby="tryit-heading" className="mx-auto max-w-5xl px-6 pb-20">
      <div className="qi-glass rounded-lg p-8">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              Try it · live
            </p>
            <p id="tryit-heading" className="font-display text-2xl text-ink-0">
              Feel a frequency.
            </p>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-aurora">Hover · Tap</p>
        </div>
        <InteractiveResonance height={180} />
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      aria-labelledby="how-it-works-heading"
      className="mx-auto max-w-6xl scroll-mt-24 space-y-10 px-6 pb-24"
    >
      <header className="text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">How it works</p>
        <h2
          id="how-it-works-heading"
          className="mt-2 text-balance font-display text-4xl text-ink-0"
        >
          Three quiet steps.
        </h2>
      </header>
      <StepRail items={STEPS} />
    </section>
  );
}

function Foundations() {
  return (
    <section
      aria-labelledby="foundations-heading"
      className="mx-auto max-w-6xl space-y-8 px-6 pb-24"
    >
      <header className="text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
          What it’s built on
        </p>
        <h2 id="foundations-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
          A tuned methodology.
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-pretty text-ink-1">
          Qi Scan pairs historical frequency traditions with modern biofeedback signal processing.
          No mystical claims — just measured response.
        </p>
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {FOUNDATIONS.map((item) => (
          <article key={item.title} className="qi-glass flex flex-col gap-3 rounded-lg p-6">
            <span
              aria-hidden="true"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-hairline bg-surface-3 text-aurora"
            >
              {item.icon}
            </span>
            <h3 className="font-display text-xl leading-none text-ink-0">{item.title}</h3>
            <p className="text-sm text-pretty text-ink-1">{item.body}</p>
          </article>
        ))}
      </div>
      <p className="mx-auto max-w-2xl text-pretty text-center text-xs text-ink-2">
        We deliberately avoid medical language. Qi Scan supports wellness optimization and
        self-knowledge, not diagnosis.
      </p>
    </section>
  );
}

function TrustStrip() {
  return (
    <section aria-labelledby="trust-heading" className="mx-auto max-w-6xl space-y-6 px-6 pb-24">
      <header className="text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
          Privacy by default
        </p>
        <h2 id="trust-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
          Your nervous system stays yours.
        </h2>
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <TrustCard
          glass
          icon={<Camera size={18} strokeWidth={1.6} />}
          title="Local-first"
          body="Webcam frames and audio stay on your device. We never upload raw biometrics."
        />
        <TrustCard
          glass
          icon={<Mic size={18} strokeWidth={1.6} />}
          title="No recording"
          body="The microphone analyzes pitch and breath in-browser. Audio buffers are discarded."
        />
        <TrustCard
          glass
          icon={<Lock size={18} strokeWidth={1.6} />}
          title="Yours to delete"
          body="One click removes your account and every scan summary. No questions."
        />
      </div>
      <p className="text-center">
        <Link
          to="/privacy"
          className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-aurora hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora/60"
        >
          Read the full privacy approach
          <span aria-hidden="true">→</span>
        </Link>
      </p>
    </section>
  );
}

function ProofPeek() {
  return (
    <section aria-labelledby="proof-heading" className="mx-auto max-w-3xl space-y-6 px-6 pb-24">
      <header className="text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Before · After</p>
        <h2 id="proof-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
          A resonance map can shift.
        </h2>
      </header>
      {/* Single qi-glass wrapper — BeforeAfterPeek is pure content now, so this
          is the only card layer (previously it was double-framed). */}
      <div className="qi-glass rounded-lg p-6 md:p-10" style={{ boxShadow: 'var(--glow-violet)' }}>
        <BeforeAfterPeek before={58} after={82} />
      </div>
    </section>
  );
}

function Differentiation() {
  return (
    <section aria-labelledby="diff-heading" className="mx-auto max-w-5xl space-y-8 px-6 pb-24">
      <header className="text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
          Why it's different
        </p>
        <h2 id="diff-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
          Most apps play a track. Qi tunes to you.
        </h2>
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Them — muted, no glow, deliberately less attractive */}
        <article className="rounded-lg border border-hairline bg-surface-1 p-6 opacity-80">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
            Most meditation apps
          </p>
          <ul className="space-y-2.5 text-sm text-ink-1">
            {THEM.map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ink-3"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </article>

        {/* Us — qi-glass + glow, the visual win */}
        <article className="qi-glass rounded-lg p-6" style={{ boxShadow: 'var(--glow-aurora)' }}>
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.18em] text-aurora">Qi Scan</p>
          <ul className="space-y-2.5 text-sm text-ink-0">
            {US.map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <Check
                  size={14}
                  strokeWidth={2.2}
                  className="mt-0.5 shrink-0 text-aurora"
                  aria-hidden="true"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </article>
      </div>
    </section>
  );
}

function Voices() {
  return (
    <section aria-labelledby="voices-heading" className="mx-auto max-w-6xl space-y-8 px-6 pb-24">
      <header className="text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
          What early users notice
        </p>
        <h2 id="voices-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
          The first thing people say.
        </h2>
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {VOICES.map((v) => (
          <blockquote
            key={v.theme}
            className="qi-glass flex flex-col justify-between gap-4 rounded-lg p-6"
          >
            <p className="font-display text-lg leading-snug text-pretty text-ink-0">"{v.quote}"</p>
            <footer className="font-mono text-xs uppercase tracking-[0.18em] text-aurora">
              {v.theme}
            </footer>
          </blockquote>
        ))}
      </div>
      <p className="text-center font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
        Paraphrased from early-user feedback
      </p>
    </section>
  );
}

function PricingTeaser() {
  return (
    <section aria-labelledby="pricing-heading" className="mx-auto max-w-5xl space-y-8 px-6 pb-24">
      <header className="space-y-2 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Pricing</p>
        <h2 id="pricing-heading" className="text-balance font-display text-4xl text-ink-0">
          Free plan starts you. Premium tunes you.
        </h2>
        <p className="mx-auto max-w-xl text-pretty text-ink-1">
          Three full scans a day stay free, forever — no card required. Premium unlocks unlimited
          sessions, the full advanced harmonic library, and your full trend history.
        </p>
      </header>

      {/* Same cards as the standalone /upgrade page — single source of truth so
          numbers, copy, and the early-access form never diverge. */}
      <PricingPlans />

      <div className="flex justify-center">
        <Link
          to="/upgrade"
          className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-aurora hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora/60"
        >
          Compare features in detail
          <span aria-hidden="true">→</span>
        </Link>
      </div>
      <p className="text-center font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
        $0 forever · No card required · Cancel any time
      </p>
    </section>
  );
}

function Faqs() {
  return (
    <section aria-labelledby="faq-heading" className="mx-auto max-w-3xl space-y-6 px-6 pb-24">
      <header className="text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Common questions</p>
        <h2 id="faq-heading" className="mt-2 text-balance font-display text-4xl text-ink-0">
          The things people ask first.
        </h2>
      </header>
      <Faq items={FAQS} />
    </section>
  );
}

function FinalCta() {
  return (
    <section aria-labelledby="final-cta-heading" className="relative overflow-hidden px-6 pb-16">
      <AuroraMesh intensity="subtle" position="bottom" />
      <div className="relative mx-auto max-w-3xl text-center">
        <h2
          id="final-cta-heading"
          className="text-balance font-display text-3xl text-ink-0 md:text-4xl"
        >
          Sixty seconds. A map of what your nervous system actually responds to.
        </h2>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/onboarding"
            className={`${buttonClasses({ variant: 'resonance', size: 'lg' })} group`}
          >
            <span>Start my free scan</span>
            <span
              aria-hidden="true"
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            >
              →
            </span>
          </Link>
          <Link to="/upgrade" className={buttonClasses({ variant: 'ghost', size: 'lg' })}>
            Compare plans
          </Link>
        </div>
        {/* Risk-reversal microcopy — answers the "what does this cost me?"
            objection right at the conversion moment. */}
        <ul
          aria-label="What it takes to start"
          className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-mono text-xs uppercase tracking-[0.18em] text-ink-3"
        >
          <li className="inline-flex items-center gap-1.5">
            <Check size={11} strokeWidth={2.2} className="text-aurora" aria-hidden="true" />
            Free first scan
          </li>
          <li className="inline-flex items-center gap-1.5">
            <Check size={11} strokeWidth={2.2} className="text-aurora" aria-hidden="true" />
            No card required
          </li>
          <li className="inline-flex items-center gap-1.5">
            <Check size={11} strokeWidth={2.2} className="text-aurora" aria-hidden="true" />
            Local-first
          </li>
          <li className="inline-flex items-center gap-1.5">
            <Check size={11} strokeWidth={2.2} className="text-aurora" aria-hidden="true" />
            ~60 seconds
          </li>
        </ul>
      </div>
    </section>
  );
}

function Disclaimer() {
  return (
    <section className="mx-auto max-w-2xl px-6 pb-16 text-center text-sm text-pretty text-ink-2">
      <p>{copy.disclaimer.long}</p>
    </section>
  );
}
